import { adminApi } from './admin-api.ts';
import { supabase } from './supabase.ts';

/**
 * The media library.
 *
 * Two halves that have to stay in step: the object lives in R2, which only
 * the API can reach, and the row lives in `media_assets`, which the browser
 * writes through PostgREST like every other record. Uploading is therefore
 * "put the object, then record it" -- in that order, so a failed insert
 * leaves an orphaned object (invisible, costs a few kilobytes) rather than a
 * library row pointing at nothing (visible, and broken on every page that
 * renders it).
 *
 * The row keeps both addresses: `storage_path` is the key a delete needs,
 * `public_url` is the string that gets written into `products.image_urls`
 * and `posts.cover_url`. Keeping the second is what makes usage answerable.
 */

/** The folders the API will accept. `content` is what the library uploads to. */
export const MEDIA_FOLDERS = [
  'content',
  'products',
  'brands',
  'categories',
  'collections',
] as const;

export type MediaFolder = (typeof MEDIA_FOLDERS)[number];

export interface MediaAsset {
  id: string;
  storagePath: string;
  publicUrl: string | null;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  alt: string | null;
  folder: string | null;
  tags: string[];
  createdAt: string;
}

const COLUMNS =
  'id, storage_path, public_url, filename, mime_type, size_bytes, width, height, alt, folder, tags, created_at';

interface MediaRow {
  id: string;
  storage_path: string;
  public_url: string | null;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  alt: string | null;
  folder: string | null;
  tags: string[];
  created_at: string;
}

function toAsset(row: MediaRow): MediaAsset {
  return {
    id: row.id,
    storagePath: row.storage_path,
    publicUrl: row.public_url,
    filename: row.filename,
    mimeType: row.mime_type,
    sizeBytes: row.size_bytes,
    width: row.width,
    height: row.height,
    alt: row.alt,
    folder: row.folder,
    tags: row.tags ?? [],
    createdAt: row.created_at,
  };
}

export async function listMedia(accountId: string): Promise<MediaAsset[]> {
  const { data, error } = await supabase
    .from('media_assets')
    .select(COLUMNS)
    .eq('account_id', accountId)
    .order('created_at', { ascending: false })
    .limit(500);

  if (error) throw error;

  return (data as MediaRow[]).map(toAsset);
}

export async function getMediaAsset(
  accountId: string,
  assetId: string,
): Promise<MediaAsset | null> {
  const { data, error } = await supabase
    .from('media_assets')
    .select(COLUMNS)
    .eq('account_id', accountId)
    .eq('id', assetId)
    .maybeSingle();

  if (error) throw error;

  return data ? toAsset(data as MediaRow) : null;
}

/**
 * Pixel dimensions, read before the file leaves the browser.
 *
 * The Worker could not do this without decoding the image, and the columns
 * are what tells a merchant their hero image is 400px wide before they
 * publish it. A file the browser cannot decode still uploads -- the
 * dimensions are simply left null.
 */
function measure(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    image.src = url;
  });
}

export interface UploadedAsset {
  url: string;
  key: string;
  /** Null when the object landed but the library row did not. */
  asset: MediaAsset | null;
}

/**
 * Put one file in R2 and record it in the library.
 *
 * Callers that only need a URL (the product image manager, the rich-text
 * editor) use the returned `url` and ignore the rest; the row exists so the
 * same image is findable in the library afterwards rather than being a
 * write-only upload.
 */
export async function uploadMedia(
  accountId: string,
  file: File,
  folder: MediaFolder = 'content',
): Promise<UploadedAsset> {
  const [uploaded, size] = await Promise.all([
    adminApi.media.upload(accountId, file, folder),
    measure(file),
  ]);

  const { data, error } = await supabase
    .from('media_assets')
    .insert({
      account_id: accountId,
      storage_path: uploaded.key,
      public_url: uploaded.url,
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      width: size?.width ?? null,
      height: size?.height ?? null,
      folder,
    })
    .select(COLUMNS)
    .maybeSingle();

  // The object is already stored and usable, so a failed row is reported and
  // not thrown: losing the library entry must not lose the upload.
  if (error) console.error('[media] uploaded but not recorded', error);

  return {
    url: uploaded.url,
    key: uploaded.key,
    asset: data ? toAsset(data as MediaRow) : null,
  };
}

export async function updateMediaAsset(
  accountId: string,
  assetId: string,
  patch: { filename?: string; alt?: string | null; folder?: string; tags?: string[] },
): Promise<void> {
  const { error } = await supabase
    .from('media_assets')
    .update(patch)
    .eq('account_id', accountId)
    .eq('id', assetId);

  if (error) throw error;
}

export interface MediaUsage {
  products: { id: string; name: string }[];
  posts: { id: string; title: string }[];
}

/**
 * Everywhere an asset's URL appears.
 *
 * Matched on the URL rather than on the id, because that is what the other
 * tables actually store -- `products.image_urls` is an array of addresses,
 * and a post's cover may have been pasted in from elsewhere. Inline images
 * inside a product description are found the only way an HTML blob allows,
 * with a substring match.
 */
export async function getMediaUsage(accountId: string, asset: MediaAsset): Promise<MediaUsage> {
  const url = asset.publicUrl;

  if (!url) return { products: [], posts: [] };

  const [inGallery, inDescription, posts] = await Promise.all([
    supabase
      .from('products')
      .select('id, name')
      .eq('account_id', accountId)
      .contains('image_urls', [url])
      .limit(50),
    supabase
      .from('products')
      .select('id, name')
      .eq('account_id', accountId)
      .ilike('description', `%${url}%`)
      .limit(50),
    supabase
      .from('posts')
      .select('id, title')
      .eq('account_id', accountId)
      .or(`cover_url.eq.${url},cover_media_id.eq.${asset.id}`)
      .limit(50),
  ]);

  if (inGallery.error) throw inGallery.error;
  if (inDescription.error) throw inDescription.error;
  if (posts.error) throw posts.error;

  const products = new Map<string, { id: string; name: string }>();

  for (const row of [...(inGallery.data ?? []), ...(inDescription.data ?? [])]) {
    products.set(row.id, { id: row.id, name: row.name });
  }

  return {
    products: [...products.values()],
    posts: (posts.data ?? []).map((row) => ({ id: row.id, title: row.title })),
  };
}

/**
 * Remove the row and then the object.
 *
 * Row first: if the object delete fails, the library is still right and the
 * leftover object is unreachable. The other order would leave a row whose
 * image 404s everywhere it is rendered.
 */
export async function deleteMediaAsset(accountId: string, asset: MediaAsset): Promise<void> {
  const { error } = await supabase
    .from('media_assets')
    .delete()
    .eq('account_id', accountId)
    .eq('id', asset.id);

  if (error) throw error;

  await adminApi.media.remove(accountId, asset.storagePath);
}
